import { type ReactNode, useState } from 'react';
import { Badge, Button, Drawer } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useBreakpoint } from './useBreakpoint';

export interface FilterDrawerProps {
  /** Filter form content (reused verbatim from the desktop filter card). */
  children: ReactNode;
  /** Number of active filters, shown as a badge on the trigger button. */
  activeCount?: number;
  onSubmit?: () => void;
  onReset?: () => void;
  submitText?: string;
  resetText?: string;
  title?: ReactNode;
  /** Desktop wrapper class (defaults to the existing filter card style). */
  desktopClassName?: string;
}

/**
 * Desktop / pad: renders the filter form inline in a card.
 * Mobile: collapses behind a "筛选" button that opens a bottom drawer.
 */
export function FilterDrawer({
  children,
  activeCount = 0,
  onSubmit,
  onReset,
  submitText = '查询',
  resetText = '重置',
  title = '筛选',
  desktopClassName = 'mall-filter-card',
}: FilterDrawerProps) {
  const breakpoint = useBreakpoint();
  const [open, setOpen] = useState(false);

  if (breakpoint !== 'mobile') {
    return <div className={desktopClassName}>{children}</div>;
  }

  return (
    <div className="mall-filter-trigger-row">
      <Badge count={activeCount} size="small">
        <Button icon={<FilterOutlined />} onClick={() => setOpen(true)}>
          {title}
        </Button>
      </Badge>
      <Drawer
        placement="bottom"
        height="auto"
        open={open}
        title={title}
        onClose={() => setOpen(false)}
        className="mall-filter-drawer"
        footer={
          <div className="mall-filter-drawer-footer">
            <Button
              block
              onClick={() => {
                onReset?.();
              }}
            >
              {resetText}
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                onSubmit?.();
                setOpen(false);
              }}
            >
              {submitText}
            </Button>
          </div>
        }
      >
        {children}
      </Drawer>
    </div>
  );
}
